'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { Plus, Download, Filter, Search, Receipt, MessageSquare, X, CheckCircle, Calendar, Clock, CreditCard } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

const formatVT = (n: number) => `VT ${n.toLocaleString('en-US')}`;

interface Repayment {
  id: string;
  loan_id: string;
  payment_date: string;
  amount_paid: number;
  remaining_balance: number;
  payment_method: string;
  notes: string;
  loan_number?: string;
  borrower_name?: string;
}

interface ActiveLoan {
  id: string;
  loan_number: string;
  remaining_balance: number;
  borrower_name?: string;
}

const methodColors: Record<string, string> = {
  Cash: 'bg-positive/10 text-positive',
  'Bank Transfer': 'bg-info/10 text-info',
  'Mobile Money': 'bg-warning/10 text-warning',
};

type ModalType = 'record' | 'receipt' | null;

export default function RepaymentsPage() {
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState<ModalType>(null);
  const [selectedPayment, setSelectedPayment] = useState<Repayment | null>(null);
  const [payments, setPayments] = useState<Repayment[]>([]);
  const [activeLoans, setActiveLoans] = useState<ActiveLoan[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ loanId: '', amount: '', method: 'Cash', date: new Date().toISOString().split('T')[0], note: '' });

  const supabase = createClient();

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [repResult, loanResult] = await Promise.all([
      supabase
        .from('repayments')
        .select('*, loans(loan_number, remaining_balance, borrowers(full_name))')
        .order('created_at', { ascending: false }),
      supabase
        .from('loans')
        .select('id, loan_number, remaining_balance, borrowers(full_name)')
        .in('status', ['Active', 'Overdue']),
    ]);

    const repData = repResult.data;
    const loanData = loanResult.data;

    const mappedPayments = (repData || []).map((r: any) => ({
      ...r,
      loan_number: r.loans?.loan_number || '—',
      borrower_name: r.loans?.borrowers?.full_name || '—',
    }));
    setPayments(mappedPayments);

    const mappedLoans = (loanData || []).map((l: any) => ({
      id: l.id,
      loan_number: l.loan_number,
      remaining_balance: Number(l.remaining_balance),
      borrower_name: l.borrowers?.full_name || '—',
    }));
    setActiveLoans(mappedLoans);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = payments.filter(p =>
    (p.borrower_name || '').toLowerCase().includes(search.toLowerCase()) ||
    (p.loan_number || '').includes(search)
  );

  const totalCollected = payments.reduce((s, p) => s + Number(p.amount_paid), 0);

  const handleRecordPayment = async () => {
    if (!form.loanId) { setError('Please select a loan'); return; }
    if (!form.amount || Number(form.amount) <= 0) { setError('Amount must be greater than 0'); return; }
    setSaving(true);
    setError(null);

    const selectedLoan = activeLoans.find(l => l.id === form.loanId);
    const newRemaining = Math.max(0, (selectedLoan?.remaining_balance || 0) - Number(form.amount));

    const { error: insertError } = await supabase
      .from('repayments')
      .insert({
        loan_id: form.loanId,
        payment_date: form.date,
        amount_paid: Number(form.amount),
        remaining_balance: newRemaining,
        payment_method: form.method as 'Cash' | 'Bank Transfer' | 'Mobile Money',
        notes: form.note.trim(),
      });

    if (!insertError) {
      // Update loan remaining balance
      const newStatus = newRemaining <= 0 ? 'Paid' : 'Active';
      await supabase
        .from('loans')
        .update({ remaining_balance: newRemaining, amount_paid: (selectedLoan?.remaining_balance || 0) - newRemaining + Number(form.amount), status: newStatus })
        .eq('id', form.loanId);
    }

    setSaving(false);
    if (insertError) {
      setError(insertError.message);
    } else {
      setForm({ loanId: '', amount: '', method: 'Cash', date: new Date().toISOString().split('T')[0], note: '' });
      setModal(null);
      fetchData();
    }
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Repayments</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Payment log, receipts, and upcoming schedule</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => { setError(null); setForm({ loanId: '', amount: '', method: 'Cash', date: new Date().toISOString().split('T')[0], note: '' }); setModal('record'); }} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">
              <Plus size={16} /> Record Repayment
            </button>
            <button className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors">
              <Receipt size={16} /> Generate Receipt
            </button>
            <button className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors">
              <MessageSquare size={16} /> Send SMS Reminder
            </button>
            <button className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors">
              <Download size={16} /> Export Payment Log
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-xs text-muted-foreground font-medium mb-1">Total Collected</p>
            <p className="text-xl font-bold text-positive font-mono">{formatVT(totalCollected)}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{payments.length} payments recorded</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-xs text-muted-foreground font-medium mb-1">Active Loans</p>
            <p className="text-xl font-bold text-foreground font-mono">{activeLoans.length}</p>
            <p className="text-xs text-muted-foreground mt-0.5">awaiting repayment</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-xs text-muted-foreground font-medium mb-1">Payments This Month</p>
            <p className="text-xl font-bold text-warning font-mono">
              {payments.filter(p => p.payment_date?.startsWith(new Date().toISOString().slice(0, 7))).length}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">recorded payments</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Payment Log */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search payments..." className="w-full pl-9 pr-4 py-2 rounded-lg bg-card border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
              </div>
              <button className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border text-sm text-muted-foreground hover:text-foreground transition-colors">
                <Filter size={15} /> Filter
              </button>
            </div>

            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Borrower</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Loan</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Date</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Amount</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Method</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Remaining</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {loading ? (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground text-sm">Loading payments...</td></tr>
                    ) : filtered.length === 0 ? (
                      <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground text-sm">No payments recorded yet.</td></tr>
                    ) : filtered.map(p => (
                      <tr key={p.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3 font-medium text-foreground">{p.borrower_name}</td>
                        <td className="px-4 py-3 font-mono text-xs text-primary">{p.loan_number}</td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">{p.payment_date}</td>
                        <td className="px-4 py-3 text-right font-mono font-semibold text-positive">{formatVT(Number(p.amount_paid))}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${methodColors[p.payment_method] || 'bg-muted text-muted-foreground'}`}>{p.payment_method}</span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-sm text-foreground">{formatVT(Number(p.remaining_balance))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Upcoming Payments */}
          <div className="space-y-4">
            <h2 className="font-semibold text-foreground text-sm flex items-center gap-2"><Calendar size={15} /> Active Loans</h2>
            <div className="space-y-3">
              {activeLoans.length === 0 ? (
                <div className="bg-card border border-border rounded-xl p-4 text-center text-muted-foreground text-sm">No active loans.</div>
              ) : activeLoans.map(loan => (
                <div key={loan.id} className="bg-card border border-border rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-semibold text-foreground text-sm">{loan.borrower_name}</p>
                    <span className="text-xs text-muted-foreground font-mono">{loan.loan_number}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={11} /> Remaining</span>
                    <span className="font-mono text-sm font-bold text-negative">{formatVT(loan.remaining_balance)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h3 className="font-bold text-foreground">
                {modal === 'record' && '+ Record Repayment'}
                {modal === 'receipt' && 'Payment Receipt'}
              </h3>
              <button onClick={() => setModal(null)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground"><X size={16} /></button>
            </div>

            {modal === 'record' && (
              <div className="p-6 space-y-4">
                {error && <div className="p-3 rounded-lg bg-negative/10 border border-negative/20 text-negative text-sm">{error}</div>}
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Select Loan *</label>
                  <select value={form.loanId} onChange={e => setForm(f => ({ ...f, loanId: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
                    <option value="">Choose active loan...</option>
                    {activeLoans.map(l => <option key={l.id} value={l.id}>{l.loan_number} — {l.borrower_name} (Rem: {formatVT(l.remaining_balance)})</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Amount Paid (VT) *</label>
                  <input value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0" type="number" min="1" className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Payment Method</label>
                  <div className="flex gap-2">
                    {['Cash', 'Bank Transfer', 'Mobile Money'].map(m => (
                      <button key={m} onClick={() => setForm(f => ({ ...f, method: m }))} className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-colors ${form.method === m ? 'bg-primary/10 border-primary text-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}>{m}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Payment Date</label>
                  <input value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} type="date" className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Note (optional)</label>
                  <input value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="e.g. Partial payment" className="w-full px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={() => setModal(null)} className="flex-1 px-4 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-muted transition-colors">Cancel</button>
                  <button onClick={handleRecordPayment} disabled={saving} className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                    {saving ? 'Saving...' : <><CheckCircle size={15} /> Record Payment</>}
                  </button>
                </div>
              </div>
            )}

            {modal === 'receipt' && selectedPayment && (
              <div className="p-6 space-y-4">
                <div className="text-center p-4 rounded-xl bg-muted/30 border border-border">
                  <div className="w-12 h-12 rounded-full bg-positive/10 border border-positive/20 flex items-center justify-center mx-auto mb-3">
                    <Receipt size={20} className="text-positive" />
                  </div>
                  <p className="font-bold text-foreground text-lg">Payment Receipt</p>
                  <p className="text-xs text-muted-foreground">{selectedPayment.id?.slice(0, 8).toUpperCase()}</p>
                </div>
                <div className="space-y-2">
                  {[
                    ['Borrower', selectedPayment.borrower_name || '—'],
                    ['Loan', selectedPayment.loan_number || '—'],
                    ['Date', selectedPayment.payment_date],
                    ['Method', selectedPayment.payment_method],
                    ['Amount Paid', formatVT(Number(selectedPayment.amount_paid))],
                    ['Remaining Balance', formatVT(Number(selectedPayment.remaining_balance))],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between text-sm py-1 border-b border-border/50">
                      <span className="text-muted-foreground">{k}</span>
                      <span className="font-medium text-foreground">{v}</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">
                    <CreditCard size={14} /> Print Receipt
                  </button>
                  <button className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors">
                    <MessageSquare size={14} /> Send SMS
                  </button>
                </div>
                <button onClick={() => setModal(null)} className="w-full px-4 py-2 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:bg-muted transition-colors">Close</button>
              </div>
            )}
          </div>
        </div>
      )}
    </AppLayout>
  );
}
